import { useState, useEffect } from 'react';

/**
 * Hook para gerenciar o layout dos botões dropdown baseado nas configurações do .env
 * Permite controlar como os botões se comportam em diferentes tamanhos de tela
 */

export type DropdownLayoutMode = 'responsive' | 'always-dropdown' | 'always-inline' | 'auto';
export type BreakpointSize = 'sm' | 'md' | 'lg' | 'xl';

interface DropdownLayoutConfig {
  mode: DropdownLayoutMode;
  breakpoint: BreakpointSize;
  forceDropdownOnSmallScreens: boolean;
}

interface DropdownLayoutState {
  shouldUseDropdown: boolean;
  breakpointClass: string;
  mobileClass: string;
  desktopClass: string;
}

const BREAKPOINT_VALUES = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
};

const BREAKPOINT_CLASSES = {
  sm: 'sm:hidden',
  md: 'md:hidden',
  lg: 'lg:hidden',
  xl: 'xl:hidden',
};

const DESKTOP_CLASSES = {
  sm: 'hidden sm:flex',
  md: 'hidden md:flex',
  lg: 'hidden lg:flex',
  xl: 'hidden xl:flex',
};

/**
 * Hook personalizado para gerenciar layout de dropdown
 * @returns Configuração e estado do layout dropdown
 */
export const useDropdownLayout = (): DropdownLayoutState & {
  config: DropdownLayoutConfig;
  updateLayout: () => void;
} => {
  // Lê configurações do .env
  const config: DropdownLayoutConfig = {
    mode: (import.meta.env.VITE_DROPDOWN_LAYOUT_MODE as DropdownLayoutMode) || 'responsive',
    breakpoint: (import.meta.env.VITE_DROPDOWN_BREAKPOINT as BreakpointSize) || 'md',
    forceDropdownOnSmallScreens: import.meta.env.VITE_FORCE_DROPDOWN_ON_SMALL_SCREENS === 'true',
  };

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  const [layoutState, setLayoutState] = useState<DropdownLayoutState>(() => 
    calculateLayoutState(config, windowWidth)
  );

  // Atualiza o estado quando a janela é redimensionada
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setWindowWidth(newWidth);
      setLayoutState(calculateLayoutState(config, newWidth));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [config]);

  // Função para forçar atualização do layout
  const updateLayout = () => {
    setLayoutState(calculateLayoutState(config, windowWidth));
  };

  return {
    ...layoutState,
    config,
    updateLayout,
  };
};

/**
 * Calcula o estado do layout baseado na configuração e largura da janela
 */
function calculateLayoutState(
  config: DropdownLayoutConfig,
  windowWidth: number
): DropdownLayoutState {
  const breakpointValue = BREAKPOINT_VALUES[config.breakpoint];
  const isSmallScreen = windowWidth < breakpointValue;

  let shouldUseDropdown = false;

  switch (config.mode) {
    case 'always-dropdown':
      shouldUseDropdown = true;
      break;
    
    case 'always-inline':
      shouldUseDropdown = false;
      break;
    
    case 'auto':
      // Lógica automática baseada no espaço disponível
      // Por simplicidade, usa o breakpoint como referência
      shouldUseDropdown = isSmallScreen;
      break;
    
    case 'responsive':
    default:
      shouldUseDropdown = isSmallScreen;
      break;
  }

  // Força dropdown em telas pequenas se configurado
  if (config.forceDropdownOnSmallScreens && isSmallScreen) {
    shouldUseDropdown = true;
  }

  return {
    shouldUseDropdown,
    breakpointClass: BREAKPOINT_CLASSES[config.breakpoint],
    mobileClass: BREAKPOINT_CLASSES[config.breakpoint],
    desktopClass: DESKTOP_CLASSES[config.breakpoint],
  };
}

/**
 * Hook simplificado que retorna apenas as classes CSS necessárias
 * @returns Classes CSS para mobile e desktop
 */
export const useDropdownClasses = () => {
  const { mobileClass, desktopClass, shouldUseDropdown } = useDropdownLayout();
  
  return {
    mobileMenuClass: mobileClass,
    desktopMenuClass: desktopClass,
    shouldUseDropdown,
    // Classes utilitárias
    dropdownContainerClass: shouldUseDropdown ? 'block' : 'hidden',
    inlineContainerClass: shouldUseDropdown ? 'hidden' : 'flex',
  };
};

/**
 * Utilitário para obter configurações de dropdown sem hook
 * Útil para componentes que não precisam de reatividade
 */
export const getDropdownConfig = (): DropdownLayoutConfig => ({
  mode: (import.meta.env.VITE_DROPDOWN_LAYOUT_MODE as DropdownLayoutMode) || 'responsive',
  breakpoint: (import.meta.env.VITE_DROPDOWN_BREAKPOINT as BreakpointSize) || 'md',
  forceDropdownOnSmallScreens: import.meta.env.VITE_FORCE_DROPDOWN_ON_SMALL_SCREENS === 'true',
});